// Alerts (plan step 3.5; Console D's #/alerts and #/alerts/:id; D103 #4 and #14): this project's
// rules out of Settings. Firing shows what fires now against its threshold (APS-303's last
// value) and the rules closest to firing; Rules holds the new-rule window (seven presets, an
// issue regressing among them), state and owner filters, mute for 1 h or 24 h, enable and
// disable; History is what alerting sent, newest first, a page at a time. A rule's page charts
// its measure against the threshold (APS-303's series), lists when it fired, the issues it
// sent, where it sends and its parameters. Destinations stay in Settings (D103: no groups yet).
import { type UseQueryResult, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type ReactNode, useId, useState } from 'react'
import { isApiError, notConfigured } from '../api/client'
import {
  alertDestinationsQuery,
  alertEventsInfiniteQuery,
  alertRuleEventsQuery,
  alertRuleSeriesQuery,
  alertRulesQuery,
  appsQuery,
  issuesQuery,
  membersQuery,
  meQuery,
  orgQuery,
  useCreateAlertRule,
  useDeleteAlertRule,
  useTestAlertDestination,
  useUpdateAlertRule,
} from '../api/queries'
import type {
  AlertDestination,
  AlertEvent,
  AlertPreset,
  AlertRule,
  AlertRuleSeries,
  AlertSlo,
  App,
} from '../api/types'
import { Confirm } from '../components/Confirm'
import {
  Bars,
  Btn,
  btnClass,
  EmptyNote,
  HovRow,
  KV,
  PageHead,
  Section,
  Seg,
  Status,
  Strip,
  type StripItem,
  TabCount,
  Table,
  TableFoot,
  Tabs,
} from '../components/d'
import { useToast } from '../components/d/Toast'
import { Icon, type IconName } from '../components/icons'
import { LineChart } from '../components/LineChart'
import { Locked } from '../components/Locked'
import { IssueTable } from '../components/Perf'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import {
  CHOICES,
  checkedEvery,
  DELIVERY,
  destinationIcon,
  eventIssue,
  eventSummary,
  FIELDS,
  formatHeadroom,
  formatMeasure,
  HANDOFF_PRESETS,
  headroom,
  KIND_LABEL,
  measureOf,
  NO_APP,
  num,
  PRESET_LABEL,
  PRESETS,
  paramOrder,
  paramsValid,
  ruleCondition,
  ruleStatus,
  ruleSummary,
  takesRelease,
} from '../lib/alerts'
import { duration, lasted } from '../lib/format'
import { closestPair, formatBurn, leftPercent } from '../lib/slo'
import { SloBurnTable, SloFired, SloRouting, sloChartMeta, sloRuns, sloStrip } from './AlertSlo'
import { isHandoff } from './HandoffSettings'
import { useProjectParams } from './params'
import { type AlertsSearch, carried } from './search'

const HOUR = 3_600_000
const DAY = 86_400_000
/** "Fired, last 90 d" on a rule's page. */
const FIRED_DAYS = 90
/** Rules shown under "Closest to firing". */
const CLOSEST = 5

/** A moment as the mock writes it: Oct 3, 09:12. */
const dateTime = (i18n: Translator, iso: string) =>
  i18n.date(iso, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

/** Who may change rules: owners and admins (members read them). */
function useCanEdit(orgId: string) {
  const org = useQuery(orgQuery(orgId))
  return {
    pending: org.isPending,
    canEdit: org.data?.role === 'owner' || org.data?.role === 'admin',
  }
}

/** A rule's scope: its app (or every app) and its release, if it names one. */
function scopeOf(r: AlertRule, apps: App[], t: Translator['t'], release = true): string {
  const app = r.app_id ? apps.find((a) => a.id === r.app_id) : undefined
  const named = typeof r.params.release === 'string' ? r.params.release : undefined
  return [app ? appLabel(app) : t('settings.alerts.allApps'), release ? named : undefined]
    .filter(Boolean)
    .join(' · ')
}

/** How a threshold reads after the value: `< 99.50 %`, `≤ -20 %`. */
const SIDE = { below: '<', above: '>', at_or_below: '≤', at_or_above: '≥' } as const

/**
 * A rule refused because the organization's plan does not include its preset (403
 * plan_required, D119), with a link to the plan as Explore's PLAN_ERRORS have.
 */
function PlanRequired({ error }: { error: unknown }) {
  const { t } = useI18n()
  if (!isApiError(error, 403) || error.code !== 'plan_required') return null
  return (
    <p role="alert" className="notice error">
      {t('alerts.planRequired')} <PlanLink />
    </p>
  )
}

/**
 * Rules the plan pauses (paused_by_plan, D119): why they are quiet, and where the plan is. Above
 * the list it counts them; on a rule's page it is that rule.
 */
function PausedByPlan({ rules, list }: { rules: { paused_by_plan?: boolean }[]; list?: boolean }) {
  const { t } = useI18n()
  const n = rules.filter((r) => r.paused_by_plan).length
  if (n === 0) return null
  return (
    <p role="status" className="notice">
      {list ? t('alerts.pausedByPlanList', { n }) : t('alerts.pausedByPlan')} <PlanLink />
    </p>
  )
}

function PlanLink() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  return (
    <Link
      className="ul"
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ orgId, projectId, section: 'plan' }}
    >
      {t('explore.plans')}
    </Link>
  )
}

/** The rule's last value against its threshold: `99.38 % < 99.50 %`; an SLO's burn: `15.2× > 14.4×`. */
function ValueVsThreshold({ rule }: { rule: AlertRule }) {
  const i18n = useI18n()
  if (rule.preset === 'slo') {
    // The pair that fires (page first), else the one closest to firing (APS-279 contract v1).
    const pairs = rule.slo?.pairs ?? []
    const p =
      pairs.find((x) => x.state === 'firing' && x.severity === 'page') ??
      pairs.find((x) => x.state === 'firing') ??
      (rule.slo ? closestPair(rule.slo) : undefined)
    if (!p || p.burn_long === null) return <span className="faint">—</span>
    return (
      <span className="al-val" translate="no">
        <b className={p.state === 'firing' ? 'c-crash' : undefined}>
          {formatBurn(i18n, p.burn_long)}
        </b>{' '}
        {/* The sign from the numbers: a pair fires only when both windows reach its rate, so
            the long window can be over it while the pair does not fire (Loom's F1 on #495). */}
        <span className="faint">
          {p.burn_long >= p.rate ? '≥' : '<'} {formatBurn(i18n, p.rate)}
        </span>
      </span>
    )
  }
  const m = measureOf(rule)
  if (!m || rule.last_value === null) return <span className="faint">—</span>
  return (
    <span className="al-val" translate="no">
      <b className={rule.state === 'firing' ? 'c-crash' : undefined}>
        {formatMeasure(i18n, rule.last_value, m.unit)}
      </b>{' '}
      <span className="faint">
        {SIDE[m.firesWhen]} {formatMeasure(i18n, m.threshold, m.unit)}
      </span>
    </span>
  )
}

/** Where a rule sends: the destination, its host, and whether sending to it fails. */
function SentTo({
  rule,
  destinations,
  canEdit,
  onPick,
  busy,
}: {
  rule: AlertRule
  destinations: AlertDestination[]
  canEdit: boolean
  onPick: (destinationId: string) => void
  busy: boolean
}) {
  const { t } = useI18n()
  const d = destinations.find((x) => x.id === rule.destination_id)
  if (d)
    return (
      <span className="al-dest">
        <Icon name={destinationIcon(d)} small />
        <span>
          {d.name}
          <span className="t2" translate="no">
            {d.host}
          </span>
          {d.consecutive_failures > 0 ? (
            <span className="t2 al-fail">
              {t('settings.alerts.failing', {
                n: d.consecutive_failures,
                code: d.last_error_code ?? '',
              })}
            </span>
          ) : null}
        </span>
      </span>
    )
  // A default rule (D62) fires into the history until someone picks where it sends.
  const pickable = destinations.filter(
    (x) => !isHandoff(x) || HANDOFF_PRESETS.includes(rule.preset),
  )
  if (canEdit && pickable.length > 0)
    return (
      <select
        className="select al-pick"
        aria-label={t('settings.alerts.chooseDestination')}
        value=""
        disabled={busy}
        onChange={(e) => e.target.value && onPick(e.target.value)}
      >
        <option value="">{t('settings.alerts.noDestination')}</option>
        {pickable.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>
    )
  return <span className="faint">{t('settings.alerts.noDestination')}</span>
}

/** Mute for an hour or a day (D103 #14), or unmute. */
function MuteButtons({
  rule,
  orgId,
  variant,
}: {
  rule: AlertRule
  orgId: string
  /** short: one hour only (the firing table); pick: 1 h or 24 h in a compact select (the rules
   * table); otherwise two buttons (a rule's page). */
  variant?: 'short' | 'pick'
}) {
  const { t } = useI18n()
  const update = useUpdateAlertRule(orgId)
  const { toast, show } = useToast()
  // An SLO mutes its pages and its tickets together, and is muted while either runs (4b v1).
  const slo = rule.preset === 'slo'
  const muted = [rule.muted_until, slo ? rule.ticket_muted_until : null].some(
    (u) => !!u && Date.parse(u) > Date.now(),
  )
  /** Sets the mute, then offers to put it back as it was (D121: Undo, not ask). */
  const setMute = (until: string | null, done: string) => {
    const before = { muted_until: rule.muted_until, ticket_muted_until: rule.ticket_muted_until }
    update.mutate(
      { ruleId: rule.id, muted_until: until, ...(slo ? { ticket_muted_until: until } : {}) },
      {
        onSuccess: () =>
          show(done, {
            label: t('toast.undo'),
            run: () =>
              update.mutate({
                ruleId: rule.id,
                muted_until: before.muted_until,
                ...(slo ? { ticket_muted_until: before.ticket_muted_until } : {}),
              }),
          }),
      },
    )
  }
  const mute = (ms: number) =>
    setMute(
      new Date(Date.now() + ms).toISOString(),
      ms >= DAY ? t('alerts.muted24h') : t('alerts.muted1h'),
    )
  // Paused by the plan (D119): nothing is checked or sent, so muting does nothing.
  if (rule.paused_by_plan && !muted) return null
  if (muted)
    return (
      <>
        <Btn
          size={variant ? 'sm' : undefined}
          variant={variant ? 'ghost' : undefined}
          disabled={update.isPending}
          onClick={() => setMute(null, t('alerts.unmuted'))}
        >
          {t('settings.alerts.unmute')}
        </Btn>
        {toast}
      </>
    )
  if (variant === 'pick')
    return (
      <>
        <select
          className="select al-mute"
          aria-label={t('alerts.mute')}
          value=""
          disabled={update.isPending || !rule.enabled}
          onChange={(e) => e.target.value && mute(e.target.value === 'day' ? DAY : HOUR)}
        >
          <option value="">{t('alerts.mute')}</option>
          <option value="hour">{t('alerts.mute1h')}</option>
          <option value="day">{t('alerts.mute24h')}</option>
        </select>
        {toast}
      </>
    )
  const short = variant === 'short'
  // The firing table: one small button; a rule's page: both, as the head's buttons.
  return (
    <>
      <Btn
        size={short ? 'sm' : undefined}
        disabled={update.isPending || !rule.enabled}
        onClick={() => mute(HOUR)}
      >
        {t('alerts.mute1h')}
      </Btn>
      {short ? null : (
        <Btn disabled={update.isPending || !rule.enabled} onClick={() => mute(DAY)}>
          {t('alerts.mute24h')}
        </Btn>
      )}
      {toast}
    </>
  )
}

// ---------------------------------------------------------------- the new rule

function RuleForm({
  orgId,
  projectId,
  destinations,
  apps,
  onClose,
}: {
  orgId: string
  projectId: string
  destinations: AlertDestination[]
  apps: App[]
  onClose: () => void
}) {
  const i18n = useI18n()
  const { t } = i18n
  const create = useCreateAlertRule(orgId)
  const [chosen, setPreset] = useState<AlertPreset>('crash_free_below')
  const [values, setValues] = useState<Record<string, string>>({})
  const [release, setRelease] = useState('')
  const [appId, setAppId] = useState('')
  const [destinationId, setDestinationId] = useState('')
  // An SLO's tickets and at risk (D119): empty sends them where its pages go.
  const [ticketDestinationId, setTicketDestinationId] = useState('')
  const [cooldown, setCooldown] = useState('60')
  const ids = {
    title: useId(),
    preset: useId(),
    app: useId(),
    destination: useId(),
    ticketDestination: useId(),
    cooldown: useId(),
    release: useId(),
    field: useId(),
  }
  const destination = destinationId || destinations[0]?.id || ''
  // A GitHub or Linear destination opens issues: only for the presets that make sense there.
  const handoff = destinations.some((d) => d.id === destination && isHandoff(d))
  const presets = handoff ? HANDOFF_PRESETS : PRESETS
  const preset = presets.includes(chosen) ? chosen : (presets[0] as AlertPreset)
  const fields = FIELDS[preset]
  const choices = CHOICES[preset] ?? []
  const params: Record<string, number | string> = {}
  for (const c of choices) {
    const v = values[`${preset}.${c.key}`] ?? c.options[0]?.value ?? ''
    params[c.key] = c.numeric ? Number(v) : v
  }
  for (const f of fields) {
    const raw = values[`${preset}.${f.key}`]
    params[f.key] = raw === undefined || raw === '' ? (f.default ?? Number.NaN) : Number(raw)
  }
  const hasRelease = takesRelease(preset, params)
  if (hasRelease && release.trim()) params.release = release.trim()
  const inRange = fields.every((f) => {
    const v = params[f.key] as number
    return Number.isFinite(v) && v >= f.min && v <= f.max
  })
  // Fields in range but contradicting each other (an SLO's page rate at or under its ticket's).
  const crossInvalid = inRange && !paramsValid(preset, params)
  const ready = destination !== '' && inRange && !crossInvalid
  const ticketDestination = preset === 'slo' ? ticketDestinationId : ''
  const app = apps.find((a) => a.id === appId)
  return (
    <form
      className="win al-newrule"
      aria-labelledby={ids.title}
      onSubmit={(e) => {
        e.preventDefault()
        if (!ready) return
        create.mutate(
          {
            project_id: projectId,
            app_id: NO_APP.includes(preset) ? null : appId || null,
            preset,
            params,
            destination_id: destination,
            ...(ticketDestination ? { ticket_destination_id: ticketDestination } : {}),
            cooldown_minutes: Number(cooldown),
          },
          { onSuccess: onClose },
        )
      }}
    >
      <div className="win-bar">
        <b id={ids.title}>{t('alerts.newRule')}</b>
        <span translate="no">{app ? appLabel(app) : t('settings.alerts.allApps')}</span>
        <span className="r">
          <Btn size="sm" variant="ghost" aria-label={t('alerts.close')} onClick={onClose}>
            <Icon name="x" small />
          </Btn>
        </span>
      </div>
      <div className="al-form">
        <label className="lbl" htmlFor={ids.preset}>
          {t('settings.alerts.preset')}
          <select
            id={ids.preset}
            className="select"
            value={preset}
            onChange={(e) => setPreset(e.target.value as AlertPreset)}
          >
            {presets.map((p) => (
              <option key={p} value={p}>
                {t(PRESET_LABEL[p])}
              </option>
            ))}
          </select>
        </label>
        {NO_APP.includes(preset) ? null : (
          <label className="lbl" htmlFor={ids.app}>
            {t('settings.alerts.app')}
            <select
              id={ids.app}
              className="select"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
            >
              <option value="">{t('settings.alerts.allApps')}</option>
              {apps.map((a) => (
                <option key={a.id} value={a.id}>
                  {appLabel(a)}
                </option>
              ))}
            </select>
          </label>
        )}
        {paramOrder(preset).map(({ choice: c, field: f }) =>
          c ? (
            <label className="lbl" htmlFor={`${ids.field}-${c.key}`} key={`${preset}.${c.key}`}>
              {t(c.label)}
              <select
                id={`${ids.field}-${c.key}`}
                className="select"
                value={String(params[c.key])}
                onChange={(e) => setValues({ ...values, [`${preset}.${c.key}`]: e.target.value })}
              >
                {c.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.label)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="lbl" htmlFor={`${ids.field}-${f.key}`} key={`${preset}.${f.key}`}>
              {t(f.label)}
              <input
                id={`${ids.field}-${f.key}`}
                className="input mono"
                type="number"
                inputMode="decimal"
                min={f.min}
                max={f.max}
                step={f.step}
                value={values[`${preset}.${f.key}`] ?? String(f.default ?? '')}
                onChange={(e) => setValues({ ...values, [`${preset}.${f.key}`]: e.target.value })}
              />
            </label>
          ),
        )}
        {hasRelease ? (
          <label className="lbl" htmlFor={ids.release}>
            {t('settings.alerts.f.release')}
            <input
              id={ids.release}
              className="input mono"
              translate="no"
              maxLength={128}
              value={release}
              onChange={(e) => setRelease(e.target.value)}
            />
          </label>
        ) : null}
        <label className="lbl" htmlFor={ids.destination}>
          {preset === 'slo' ? t('settings.alerts.f.pageTo') : t('alerts.sendTo')}
          <select
            id={ids.destination}
            className="select"
            value={destination}
            onChange={(e) => setDestinationId(e.target.value)}
          >
            {destinations.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        {preset === 'slo' ? (
          <label className="lbl" htmlFor={ids.ticketDestination}>
            {t('settings.alerts.f.ticketTo')}
            <select
              id={ids.ticketDestination}
              className="select"
              value={ticketDestinationId}
              onChange={(e) => setTicketDestinationId(e.target.value)}
            >
              <option value="">{t('settings.alerts.sameAsPage')}</option>
              {destinations
                .filter((d) => !isHandoff(d))
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
        <label className="lbl" htmlFor={ids.cooldown}>
          {t('settings.alerts.cooldown')}
          <select
            id={ids.cooldown}
            className="select"
            value={cooldown}
            onChange={(e) => setCooldown(e.target.value)}
          >
            {[15, 60, 240, 1440].map((m) => (
              <option key={m} value={String(m)}>
                {t('settings.alerts.minutes', { n: m })}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="al-form-f">
        <code translate="no">{ruleCondition({ preset, params })}</code>
        <span className="r">
          <Btn onClick={onClose}>{t('settings.cancel')}</Btn>
          <Btn type="submit" variant="solid" disabled={!ready || create.isPending}>
            {t('alerts.createRule')}
          </Btn>
        </span>
      </div>
      {crossInvalid ? (
        <p role="alert" className="al-form-err">
          {t('settings.alerts.pageAboveTicket')}
        </p>
      ) : null}
      {isApiError(create.error, 403) && create.error.code === 'plan_required' ? (
        <PlanRequired error={create.error} />
      ) : create.error ? (
        <p role="alert" className="al-form-err">
          {isApiError(create.error, 400)
            ? t('settings.alerts.ruleInvalid')
            : isApiError(create.error, 402)
              ? t('settings.alerts.tooManyRules')
              : t('state.failed')}
        </p>
      ) : null}
    </form>
  )
}

// ---------------------------------------------------------------- the list

export function AlertsPage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as AlertsSearch
  const tab = search.tab ?? 'firing'
  const { canEdit, pending } = useCanEdit(orgId)
  const rules = useQuery(alertRulesQuery(orgId, projectId))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  const apps = useQuery(appsQuery(orgId, projectId))
  const [creating, setCreating] = useState(false)
  const params = { orgId, projectId }
  const title = t('nav.alerts')

  const set = (patch: Partial<AlertsSearch>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) })

  if (rules.isPending || pending)
    return (
      <main id="main" className="pg al-pg">
        <PageHead title={title} />
        <Loading />
      </main>
    )
  // Alerting is off on this server (no ALERTS_KEY): its own page, no empty tabs.
  if (rules.isError && notConfigured(rules.error))
    return (
      <main id="main" className="pg al-pg">
        <PageHead title={title} />
        <EmptyNote title={t('settings.alerts.off')} />
      </main>
    )
  if (rules.isError)
    return (
      <main id="main" className="pg al-pg">
        <PageHead title={title} />
        <ErrorState error={rules.error} retry={() => rules.refetch()} />
      </main>
    )

  const list = rules.data.rules
  const dests = destinations.data?.destinations ?? []
  const now = Date.now()
  const firing = list.filter((r) => ruleStatus(r, now, i18n).key === 'firing')
  const atRisk = list.filter((r) => ruleStatus(r, now, i18n).key === 'at_risk')
  const failing = dests.filter((d) => d.consecutive_failures > 0)
  const meta = [
    t('alerts.meta.firing', { count: firing.length }),
    ...(atRisk.length > 0 ? [t('alerts.meta.atRisk', { count: atRisk.length })] : []),
    t('alerts.meta.rules', { count: list.length }),
    t('alerts.meta.destinations', { count: dests.length }),
  ].join(' · ')
  const tabLink = (k: (typeof TABS)[number], label: string, n?: number) => (
    <Link
      key={k}
      to="/o/$orgId/p/$projectId/alerts"
      params={params}
      search={(prev: Record<string, unknown>) => ({
        ...carried(prev as AlertsSearch),
        ...(k === 'firing' ? {} : { tab: k }),
      })}
      aria-current={tab === k ? 'page' : undefined}
    >
      {label}
      {n !== undefined ? (
        <>
          {' '}
          <TabCount n={n} />
        </>
      ) : null}
    </Link>
  )

  return (
    <main id="main" className="pg al-pg">
      <PageHead
        title={title}
        meta={meta}
        actions={
          <>
            <Link
              className={btnClass()}
              to="/o/$orgId/p/$projectId/settings/$section"
              params={{ ...params, section: 'alerts' }}
            >
              <Icon name="people" />
              {t('alerts.destinations')}
            </Link>
            {canEdit ? (
              <Btn
                variant="solid"
                onClick={() => {
                  setCreating(true)
                  if (tab !== 'rules') set({ tab: 'rules' })
                }}
              >
                <Icon name="plus" />
                {t('alerts.newRule')}
              </Btn>
            ) : pending ? null : (
              <Locked label={t('alerts.newRule')} />
            )}
          </>
        }
      />
      <section className="sec" aria-label={title}>
        <Tabs label={title}>
          {tabLink('firing', t('alerts.tab.firing'), firing.length)}
          {tabLink('rules', t('alerts.tab.rules'), list.length)}
          {tabLink('history', t('alerts.tab.history'))}
        </Tabs>
        {tab === 'firing' ? (
          <FiringPanel
            orgId={orgId}
            rules={list}
            firing={firing}
            destinations={dests}
            apps={apps.data ?? []}
            failing={failing.length}
            canEdit={canEdit}
          />
        ) : tab === 'rules' ? (
          <RulesPanel
            orgId={orgId}
            projectId={projectId}
            rules={list}
            destinations={dests}
            apps={apps.data ?? []}
            canEdit={canEdit}
            creating={creating && canEdit}
            onCreating={setCreating}
            search={search}
            set={set}
          />
        ) : (
          <HistoryPanel orgId={orgId} projectId={projectId} rules={list} destinations={dests} />
        )}
      </section>
    </main>
  )
}

const TABS = ['firing', 'rules', 'history'] as const

function FiringPanel({
  orgId,
  rules,
  firing,
  destinations,
  apps,
  failing,
  canEdit,
}: {
  orgId: string
  rules: AlertRule[]
  firing: AlertRule[]
  destinations: AlertDestination[]
  apps: App[]
  failing: number
  canEdit: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { projectId } = useProjectParams()
  const navigate = useNavigate()
  const update = useUpdateAlertRule(orgId)
  const now = Date.now()
  const toRule = (r: AlertRule) => ({
    to: '/o/$orgId/p/$projectId/alerts/$ruleId' as const,
    params: { orgId, projectId, ruleId: r.id },
  })
  // The rules not firing, nearest their threshold first (APS-303's last value).
  // A rule the plan pauses has a last value from before: not close to anything now.
  const closest = rules
    .filter((r) => r.enabled && r.state !== 'firing' && !r.paused_by_plan)
    .map((r) => ({ r, h: headroom(r) }))
    .filter((x): x is { r: AlertRule; h: NonNullable<ReturnType<typeof headroom>> } => !!x.h)
    .sort((a, b) => a.h.share - b.h.share)
    .slice(0, CLOSEST)
  return (
    <div className="al-pn">
      {firing.length === 0 ? (
        <EmptyNote title={t('alerts.noneFiring')}>{t('alerts.noneFiringBody')}</EmptyNote>
      ) : (
        <Table label={t('alerts.firingNow')} rowKeys className="al-tbl">
          <thead>
            <tr>
              <th scope="col">{t('settings.alerts.status')}</th>
              <th scope="col">{t('settings.alerts.rule')}</th>
              <th scope="col">{t('alerts.col.scope')}</th>
              <th scope="col">{t('alerts.col.value')}</th>
              <th scope="col">{t('alerts.col.since')}</th>
              <th scope="col">{t('alerts.sendTo')}</th>
              <th scope="col">
                <span className="visually-hidden">{t('settings.keys.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {firing.map((r) => (
              <HovRow key={r.id} onOpen={() => navigate(toRule(r))}>
                <td>
                  <Status s="fire" />
                </td>
                <td className="al-rule wrap">
                  <Link {...toRule(r)}>{t(PRESET_LABEL[r.preset])}</Link>
                  <span className="t2" translate="no">
                    {ruleCondition(r)}
                  </span>
                </td>
                <td className="wrap" translate="no">
                  {scopeOf(r, apps, t)}
                </td>
                <td>
                  <ValueVsThreshold rule={r} />
                </td>
                <td className="wrap">
                  {i18n.date(r.state_since, { timeStyle: 'short' })}{' '}
                  <span className="faint">
                    · {duration(i18n, Math.max(0, now - Date.parse(r.state_since)))}
                  </span>
                </td>
                <td className="al-sent wrap">
                  <SentTo
                    rule={r}
                    destinations={destinations}
                    canEdit={canEdit}
                    busy={update.isPending}
                    onPick={(d) => update.mutate({ ruleId: r.id, destination_id: d })}
                  />
                </td>
                <td className="al-acts">
                  {canEdit ? <MuteButtons rule={r} orgId={orgId} variant="short" /> : null}
                  <Link className={btnClass(undefined, 'sm')} {...toRule(r)}>
                    {t('alerts.open')}
                  </Link>
                </td>
              </HovRow>
            ))}
          </tbody>
        </Table>
      )}
      {failing > 0 ? (
        <TableFoot end>
          <span className="dim">{t('alerts.destinationsFailing', { count: failing })}</span>
        </TableFoot>
      ) : null}
      {closest.length > 0 ? (
        <Section title={t('alerts.closest')} meta={t('alerts.closestMeta')} className="al-closest">
          <Table label={t('table.of', { name: t('alerts.closest') })} className="al-tbl">
            <thead>
              <tr>
                <th scope="col">{t('settings.alerts.rule')}</th>
                <th scope="col">{t('alerts.col.watching')}</th>
                <th scope="col" className="num">
                  {t('alerts.col.now')}
                </th>
                <th scope="col" className="num">
                  {t('alerts.col.threshold')}
                </th>
                <th scope="col" className="num">
                  {t('alerts.col.headroom')}
                </th>
                <th scope="col">{t('settings.alerts.status')}</th>
              </tr>
            </thead>
            <tbody>
              {closest.map(({ r, h }) => {
                const s = ruleStatus(r, now, i18n)
                return (
                  <HovRow key={r.id} onOpen={() => navigate(toRule(r))}>
                    <td>
                      <Link {...toRule(r)}>{t(PRESET_LABEL[r.preset])}</Link>
                    </td>
                    <td>
                      {r.preset === 'slo' && r.slo ? (
                        // An SLO watches its budget (Console D): the rule column has the rest.
                        sloWatching(r.slo, i18n)
                      ) : (
                        <>
                          {ruleSummary(r, i18n)}
                          <span className="faint" translate="no">
                            {' · '}
                            {scopeOf(r, apps, t, false)}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="num" translate="no">
                      {formatMeasure(i18n, h.now ?? (r.last_value as number), h.measure.unit)}
                    </td>
                    <td className="num" translate="no">
                      {formatMeasure(i18n, h.measure.threshold, h.measure.unit)}
                    </td>
                    <td className="num" translate="no">
                      {formatHeadroom(i18n, h)}
                    </td>
                    <td>
                      <Status s={s.s} label={s.label} />
                    </td>
                  </HovRow>
                )
              })}
            </tbody>
          </Table>
        </Section>
      ) : null}
    </div>
  )
}

function RulesPanel({
  orgId,
  projectId,
  rules,
  destinations,
  apps,
  canEdit,
  creating,
  onCreating,
  search,
  set,
}: {
  orgId: string
  projectId: string
  rules: AlertRule[]
  destinations: AlertDestination[]
  apps: App[]
  canEdit: boolean
  creating: boolean
  onCreating: (v: boolean) => void
  search: AlertsSearch
  set: (patch: Partial<AlertsSearch>) => void
}) {
  const i18n = useI18n()
  const { t } = i18n
  const navigate = useNavigate()
  const update = useUpdateAlertRule(orgId)
  const me = useQuery(meQuery)
  const members = useQuery(membersQuery(orgId))
  const myId = me.data?.user.id
  const now = Date.now()
  const nameOf = (id: string | null) => {
    if (!id) return undefined
    const m = members.data?.find((x) => x.user_id === id)
    return m ? m.name || m.email : undefined
  }
  const shown = rules.filter(
    (r) =>
      (!search.state || ruleStatus(r, now, i18n).key === search.state) &&
      (!search.mine || r.created_by === myId),
  )
  const toRule = (r: AlertRule) => ({
    to: '/o/$orgId/p/$projectId/alerts/$ruleId' as const,
    params: { orgId, projectId, ruleId: r.id },
  })
  return (
    <div className="al-pn">
      {creating ? (
        destinations.length > 0 ? (
          <RuleForm
            orgId={orgId}
            projectId={projectId}
            destinations={destinations}
            apps={apps}
            onClose={() => onCreating(false)}
          />
        ) : (
          <EmptyNote title={t('settings.alerts.needDestination')}>
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/settings/$section"
              params={{ orgId, projectId, section: 'alerts' }}
            >
              {t('settings.alerts.addDestination')}
            </Link>
          </EmptyNote>
        )
      ) : null}
      <PlanRequired error={update.error} />
      <PausedByPlan rules={rules} list />
      <div className="al-rbar">
        <Seg
          label={t('alerts.filter.state')}
          options={[
            { value: 'all', label: t('alerts.filter.all') },
            { value: 'firing', label: t('alerts.filter.firing') },
            { value: 'at_risk', label: t('alerts.filter.atRisk') },
            { value: 'ok', label: t('alerts.filter.ok') },
            { value: 'muted', label: t('alerts.filter.muted') },
            { value: 'off', label: t('alerts.filter.off') },
          ]}
          value={search.state ?? 'all'}
          onChange={(v) => set({ state: v === 'all' ? undefined : v })}
        />
        <Seg
          label={t('alerts.filter.owner')}
          options={[
            { value: 'all', label: t('alerts.filter.everyone') },
            { value: 'mine', label: t('alerts.filter.mine') },
          ]}
          value={search.mine ? 'mine' : 'all'}
          onChange={(v) => set({ mine: v === 'mine' ? true : undefined })}
        />
      </div>
      {rules.length === 0 ? (
        <EmptyNote title={t('settings.alerts.noRules')} />
      ) : shown.length === 0 ? (
        <EmptyNote title={t('alerts.noMatch')}>{t('alerts.noMatchBody')}</EmptyNote>
      ) : (
        <Table label={t('settings.alerts.rules')} rowKeys className="al-tbl al-rules">
          <thead>
            <tr>
              <th scope="col">{t('settings.alerts.name')}</th>
              <th scope="col">{t('alerts.col.condition')}</th>
              <th scope="col">{t('alerts.sendTo')}</th>
              <th scope="col">{t('alerts.col.lastFired')}</th>
              <th scope="col">{t('alerts.col.stateOwner')}</th>
              <th scope="col">
                <span className="visually-hidden">{t('settings.keys.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const s = ruleStatus(r, now, i18n)
              const owner = nameOf(r.created_by)
              return (
                <HovRow key={r.id} onOpen={() => navigate(toRule(r))}>
                  <td className="al-rule wrap">
                    <Link {...toRule(r)}>{t(PRESET_LABEL[r.preset])}</Link>
                    <span className="t2">
                      {ruleSummary(r, i18n)}
                      <span translate="no">
                        {' · '}
                        {scopeOf(r, apps, t, false)}
                      </span>
                    </span>
                    {r.seeded ? (
                      <span className="t2" title={t('settings.alerts.seededHint')}>
                        {t('alerts.default')}
                      </span>
                    ) : null}
                  </td>
                  <td className="al-cond">
                    <code translate="no">{ruleCondition(r)}</code>
                  </td>
                  <td className="al-sent wrap">
                    <SentTo
                      rule={r}
                      destinations={destinations}
                      canEdit={canEdit}
                      busy={update.isPending}
                      onPick={(d) => update.mutate({ ruleId: r.id, destination_id: d })}
                    />
                  </td>
                  <td>
                    {r.last_fired_at ? (
                      dateTime(i18n, r.last_fired_at)
                    ) : (
                      <span className="faint">{t('alerts.never')}</span>
                    )}
                  </td>
                  <td>
                    <Status s={s.s} label={s.label} />
                    {r.last_error ? (
                      <span className="t2">
                        {t('settings.alerts.skipped', { code: r.last_error })}
                      </span>
                    ) : null}
                    {owner ? (
                      <span className="t2">
                        {r.created_by === myId
                          ? t('alerts.ownerYou', { name: owner })
                          : t('alerts.owner', { name: owner })}
                      </span>
                    ) : null}
                  </td>
                  <td className="al-acts">
                    {canEdit ? (
                      <>
                        <MuteButtons rule={r} orgId={orgId} variant="pick" />
                        <Btn
                          size="sm"
                          disabled={update.isPending}
                          onClick={() => update.mutate({ ruleId: r.id, enabled: !r.enabled })}
                        >
                          {r.enabled ? t('settings.alerts.disable') : t('settings.alerts.enable')}
                        </Btn>
                      </>
                    ) : null}
                  </td>
                </HovRow>
              )
            })}
          </tbody>
        </Table>
      )}
      <TableFoot>
        <span>{t('alerts.checkedFoot')}</span>
        {canEdit ? null : <span>{t('settings.adminOnly')}</span>}
      </TableFoot>
    </div>
  )
}

const EVENT_ICON: Record<AlertEvent['kind'], IconName> = {
  fired: 'alerts',
  resolved: 'check',
  new_issue: 'issues',
  issue_regressed: 'issues',
  issue_resolved: 'check',
  test: 'bolt',
  suppressed: 'alerts',
  at_risk: 'alerts',
  at_risk_cleared: 'check',
}

function HistoryPanel({
  orgId,
  projectId,
  rules,
  destinations,
}: {
  orgId: string
  projectId: string
  rules: AlertRule[]
  destinations: AlertDestination[]
}) {
  const { t } = useI18n()
  const events = useInfiniteQuery(alertEventsInfiniteQuery(orgId, projectId))
  if (events.isPending) return <Loading />
  if (events.isError) return <ErrorState error={events.error} retry={() => events.refetch()} />
  const rows = events.data.pages.flatMap((p) => p.events)
  return (
    <div className="al-pn">
      {rows.length === 0 ? (
        <EmptyNote title={t('settings.alerts.noHistory')} />
      ) : (
        <EventFeed events={rows} rules={rules} destinations={destinations} />
      )}
      <TableFoot>
        <span>{t('alerts.historyFoot')}</span>
        {events.hasNextPage ? (
          <Btn
            size="sm"
            variant="ghost"
            disabled={events.isFetchingNextPage}
            onClick={() => events.fetchNextPage()}
          >
            {t('alerts.loadMore')}
          </Btn>
        ) : null}
      </TableFoot>
    </div>
  )
}

/** What alerting did: the rule, what happened, the issue, then the delivery and when. */
function EventFeed({
  events,
  rules,
  destinations,
  noRule,
}: {
  events: AlertEvent[]
  rules: AlertRule[]
  destinations: AlertDestination[]
  /** A rule's own page: the rule goes without saying. */
  noRule?: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as AlertsSearch
  return (
    <ul className="feed al-feed">
      {events.map((e) => {
        const rule = rules.find((r) => r.id === e.rule_id)
        const dest = destinations.find((d) => d.id === e.destination_id)
        const issue = eventIssue(e)
        const release = typeof e.detail.release === 'string' ? e.detail.release : undefined
        const d = DELIVERY[e.delivery]
        return (
          <li key={e.id}>
            <span className={e.delivery === 'failed' ? 'fi al-bad' : 'fi'}>
              <Icon name={EVENT_ICON[e.kind]} />
            </span>
            <span>
              {rule && !noRule ? (
                <>
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/alerts/$ruleId"
                    params={{ orgId, projectId, ruleId: rule.id }}
                  >
                    {t(PRESET_LABEL[rule.preset])}
                  </Link>
                  {': '}
                </>
              ) : null}
              {eventSummary(e, i18n)}
              {issue ? (
                <>
                  {' · '}
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/issues/$issueId"
                    params={{ orgId, projectId, issueId: issue }}
                    search={carried(search)}
                    translate="no"
                  >
                    {issue.slice(0, 8)}
                  </Link>
                </>
              ) : null}
              {release ? (
                <>
                  {' · '}
                  <code translate="no">{release}</code>
                </>
              ) : null}
              <span className="al-dl">
                <Status s={d.s} label={t(d.label)} />
                {dest ? ` ${dest.name}` : ''}
                {e.error_code ? (
                  <>
                    {' · '}
                    <code translate="no">{e.error_code}</code>
                  </>
                ) : null}
                {e.attempts > 1 ? ` · ${t('alerts.attempts', { n: e.attempts })}` : ''}
              </span>
            </span>
            <time dateTime={e.created_at}>{dateTime(i18n, e.created_at)}</time>
          </li>
        )
      })}
    </ul>
  )
}

// ---------------------------------------------------------------- one rule

/** A parameter's name on a rule's page, without the form's unit in brackets. */
function fieldLabel(preset: AlertPreset, key: string): MessageId {
  switch (key) {
    case 'threshold_percent':
      return 'alerts.kv.threshold'
    case 'window_hours':
    case 'window_days':
      return 'alerts.kv.window'
    case 'factor':
      return 'alerts.kv.factor'
    case 'change_percent':
      return 'alerts.kv.change'
    case 'device_threshold_percent':
      return 'alerts.kv.device'
    case 'min_users':
      return preset === 'new_issue' ? 'alerts.kv.users' : 'alerts.kv.minimum'
    case 'min_events':
      return 'alerts.kv.minEvents'
    case 'objective':
      return 'alerts.kv.objective'
    case 'page_rate':
      return 'alerts.kv.page'
    case 'ticket_rate':
      return 'alerts.kv.ticket'
    case 'at_risk_below':
      return 'alerts.kv.atRisk'
    default:
      return 'alerts.kv.minimum'
  }
}

/** A parameter's value with its unit, as the mock: 99.50 %, 24 h, 100 sessions, 5×. */
function fieldValue(
  i18n: Translator,
  key: string,
  v: number,
  params: Record<string, unknown> = {},
): string {
  const { t, number } = i18n
  // metric_change counts sessions and crashes, or requests and failures (D119).
  const failures = params.metric === 'endpoint_failure_rate'
  switch (key) {
    case 'min_volume':
      return t(failures ? 'alerts.unit.requests' : 'alerts.unit.sessions', { n: v })
    case 'min_events':
      return t(failures ? 'alerts.unit.failures' : 'alerts.unit.crashes', { n: v })
    case 'threshold_percent':
    case 'device_threshold_percent':
      return `${number(v, { minimumFractionDigits: v >= 90 ? 2 : 1, maximumFractionDigits: 2 })} %`
    case 'window_hours':
      return t('alerts.unit.hours', { n: number(v) })
    case 'window_days':
      return t('alerts.unit.days', { n: number(v) })
    case 'min_sessions':
      return t('alerts.unit.sessions', { n: v })
    case 'min_occurrences':
      return t('alerts.unit.occurrences', { n: v })
    case 'min_requests':
      return t('alerts.unit.requests', { n: v })
    case 'factor':
      return `${number(v)}×`
    case 'change_percent':
      return `${number(v, { maximumFractionDigits: 1 })} %`
    // An SLO (D119): its objective, the two burn rates with their fixed windows, at risk.
    case 'objective':
      return t('alerts.unit.objective', {
        n: number(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      })
    case 'page_rate':
      return t('alerts.unit.pageRate', { rate: number(v) })
    case 'ticket_rate':
      return t('alerts.unit.ticketRate', { rate: number(v) })
    case 'at_risk_below':
      return t('alerts.unit.budgetBelow', { n: number(v) })
    case 'min_users':
      return v === 0
        ? t('alerts.unit.users', { n: 0 })
        : t('alerts.unit.userDays', { n: number(v) })
    case 'min_device_users':
      return t('alerts.unit.userDays', { n: number(v) })
    default:
      return number(v)
  }
}

/** What a rule's chart shows, by preset. */
const CHART_TITLE = {
  crash_free_below: 'alerts.chart.crashFree',
  endpoint_failure_above: 'alerts.chart.endpointRate',
  store_crash_rate: 'alerts.chart.storeCrash',
  store_anr_rate: 'alerts.chart.storeAnr',
  issue_spike: 'alerts.chart.factor',
  new_issue: 'alerts.chart.count',
  issue_regressed: 'alerts.chart.count',
  metric_change: 'alerts.chart.change',
  slo: 'alerts.chart.budget',
} as const satisfies Record<AlertPreset, string>

/** When each firing ended: the resolution after it, or none while it goes on. */
export function firings(events: AlertEvent[]): { fired: AlertEvent; resolved?: AlertEvent }[] {
  const asc = [...events].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
  const out: { fired: AlertEvent; resolved?: AlertEvent }[] = []
  for (const e of asc) {
    if (e.kind === 'fired') out.push({ fired: e })
    else if (e.kind === 'resolved') {
      const open = [...out].reverse().find((x) => !x.resolved)
      if (open) open.resolved = e
    }
  }
  return out.reverse()
}

export function AlertRulePage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const { ruleId = '' } = useParams({ strict: false }) as { ruleId?: string }
  const { canEdit } = useCanEdit(orgId)
  const rules = useQuery(alertRulesQuery(orgId, projectId))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  const apps = useQuery(appsQuery(orgId, projectId))
  const members = useQuery(membersQuery(orgId))
  const events = useQuery({ ...alertRuleEventsQuery(orgId, projectId, ruleId), enabled: !!ruleId })
  const series = useQuery({ ...alertRuleSeriesQuery(orgId, ruleId), enabled: !!ruleId })
  const update = useUpdateAlertRule(orgId)
  const remove = useDeleteAlertRule(orgId)
  const test = useTestAlertDestination(orgId)
  // A crash-free rule's crash issues on its release, by sessions (the mock's "Issues it linked").
  const watched = rules.data?.rules.find((x) => x.id === ruleId)
  const crashIssues = useQuery({
    ...issuesQuery(projectId, {
      ...(watched?.app_id ? { app_id: watched.app_id } : {}),
      ...(typeof watched?.params.release === 'string' ? { release: watched.params.release } : {}),
      kind: 'crash',
      sort: 'sessions',
      days: 7,
      limit: 5,
      daily: true,
    }),
    // An SLO lists the crash issues spending its budget, as crash-free does (Console D).
    enabled: watched?.preset === 'crash_free_below' || watched?.preset === 'slo',
  })
  const params = { orgId, projectId }
  const crumb = (
    <>
      <Link to="/o/$orgId/p/$projectId/alerts" params={params}>
        {t('nav.alerts')}
      </Link>
      {' / '}
      <span translate="no">{ruleId.slice(0, 8)}</span>
    </>
  )

  if (rules.isPending)
    return (
      <main id="main" className="pg al-pg">
        <PageHead crumb={crumb} title={t('nav.alerts')} />
        <Loading />
      </main>
    )
  if (rules.isError)
    return (
      <main id="main" className="pg al-pg">
        <PageHead crumb={crumb} title={t('nav.alerts')} />
        {notConfigured(rules.error) ? (
          <EmptyNote title={t('settings.alerts.off')} />
        ) : (
          <ErrorState error={rules.error} retry={() => rules.refetch()} />
        )}
      </main>
    )
  const r = rules.data.rules.find((x) => x.id === ruleId)
  if (!r)
    return (
      <main id="main" className="pg al-pg">
        <PageHead crumb={crumb} title={t('nav.alerts')} />
        <EmptyNote title={t('alerts.rule.missing')}>
          <Link className={btnClass()} to="/o/$orgId/p/$projectId/alerts" params={params}>
            {t('alerts.all')}
          </Link>
        </EmptyNote>
      </main>
    )

  const now = Date.now()
  const s = ruleStatus(r, now, i18n)
  const paused = s.key === 'paused'
  const dests = destinations.data?.destinations ?? []
  const dest = dests.find((d) => d.id === r.destination_id)
  const m = measureOf(r)
  const evs = events.data?.events ?? []
  const since90 = now - FIRED_DAYS * DAY
  const recent = evs.filter((e) => Date.parse(e.created_at) >= since90)
  const sends = recent.filter((e) => ['fired', 'new_issue', 'issue_regressed'].includes(e.kind))
  const delivered = sends.filter((e) => e.delivery === 'sent').length
  const runs = firings(recent)
  // An SLO's spells, per pair and at risk (APS-279 v1), in place of firings.
  const sloSpells = r.preset === 'slo' ? sloRuns(recent) : []
  const lastIssue = evs.map(eventIssue).find(Boolean)
  const owner = r.created_by ? members.data?.find((x) => x.user_id === r.created_by) : undefined
  const openIssue = lastIssue ?? crashIssues.data?.issues[0]?.id
  const issues = [
    ...new Map(
      evs
        .filter((e) => eventIssue(e))
        .map((e) => [eventIssue(e) as string, e] as const)
        .reverse(),
    ).values(),
  ].reverse()

  const slo = r.preset === 'slo' ? (r.slo ?? null) : null
  const strip: StripItem[] =
    r.preset === 'slo'
      ? sloStrip(r, i18n)
      : m
        ? [
            {
              dt: t('alerts.col.now'),
              dd:
                r.last_value === null
                  ? '—'
                  : formatMeasure(i18n, r.last_value, m.unit).replace(/\s?[%×]$/, ''),
              small: r.last_value === null ? undefined : m.unit === 'factor' ? '×' : '%',
              // Paused by the plan (D119): not checked, so neither past nor within its threshold.
              d: paused ? (
                <span className="dim">{t('alerts.notChecked')}</span>
              ) : (
                <>
                  <span className={r.state === 'firing' ? 'c-crash' : undefined}>
                    {r.state === 'firing' ? t('alerts.breached') : t('alerts.within')}
                  </span>{' '}
                  {SIDE[m.firesWhen]} {formatMeasure(i18n, m.threshold, m.unit)}
                </>
              ),
            },
            r.state === 'firing' && !paused
              ? {
                  dt: t('alerts.firingFor'),
                  dd: duration(i18n, Math.max(0, now - Date.parse(r.state_since))),
                  d: t('alerts.sinceAt', { time: dateTime(i18n, r.state_since) }),
                }
              : {
                  dt: t('alerts.col.lastFired'),
                  dd: r.last_fired_at
                    ? i18n.date(r.last_fired_at, { month: 'short', day: 'numeric' })
                    : '—',
                  d: r.last_fired_at
                    ? i18n.date(r.last_fired_at, { timeStyle: 'short' })
                    : t('alerts.never'),
                },
            {
              dt: t('alerts.fired90'),
              dd: i18n.number(runs.length),
              d:
                runs.length === 0
                  ? t('alerts.neverFired')
                  : t('alerts.firedTimes', { count: runs.length }),
            },
            {
              // Firing, the distance is how far past the threshold it is.
              dt: (headroom(r)?.value ?? 0) < 0 ? t('alerts.pastBy') : t('alerts.col.headroom'),
              dd: (() => {
                const h = headroom(r)
                return h ? formatHeadroom(i18n, { ...h, value: Math.abs(h.value) }) : '—'
              })(),
              d: t('alerts.thresholdLine', { value: formatMeasure(i18n, m.threshold, m.unit) }),
            },
          ]
        : [
            {
              dt: t('alerts.col.lastFired'),
              dd: r.last_fired_at
                ? i18n.date(r.last_fired_at, { month: 'short', day: 'numeric' })
                : '—',
              d: r.last_fired_at
                ? [i18n.date(r.last_fired_at, { timeStyle: 'short' }), lastIssue?.slice(0, 8)]
                    .filter(Boolean)
                    .join(' · ')
                : t('alerts.never'),
            },
            {
              dt: t('alerts.fired90'),
              dd: i18n.number(sends.length),
              d:
                r.preset === 'issue_regressed'
                  ? t('alerts.perRegression')
                  : t('alerts.perNewIssue'),
            },
            {
              dt: t('settings.alerts.delivery'),
              dd: i18n.number(delivered),
              small: t('alerts.ofCount', { n: i18n.number(sends.length) }),
              d: dest
                ? t('alerts.deliveredTo', { name: dest.name })
                : t('settings.alerts.noDestination'),
            },
          ]

  return (
    <main id="main" className="pg al-pg">
      <PageHead
        crumb={crumb}
        title={<span translate="no">{ruleCondition(r)}</span>}
        variant="mono"
        meta={
          <>
            <Status s={s.s} label={s.label} /> · {t(PRESET_LABEL[r.preset])} ·{' '}
            <span translate="no">{scopeOf(r, apps.data ?? [], t)}</span> ·{' '}
            {t('alerts.checked', { every: checkedEvery(r.preset) })}
            {r.last_error ? (
              <>
                {' · '}
                {t('settings.alerts.skipped', { code: r.last_error })}
              </>
            ) : null}
          </>
        }
        actions={
          canEdit ? (
            <>
              <MuteButtons rule={r} orgId={orgId} />
              {dest ? (
                <Btn disabled={test.isPending} onClick={() => test.mutate(dest.id)}>
                  {t('settings.alerts.test')}
                </Btn>
              ) : null}
              <Btn
                disabled={update.isPending}
                onClick={() => update.mutate({ ruleId: r.id, enabled: !r.enabled })}
              >
                {r.enabled ? t('settings.alerts.disable') : t('settings.alerts.enable')}
              </Btn>
              <Confirm
                label={t('settings.alerts.delete')}
                title={t('settings.alerts.deleteRuleTitle')}
                body={t('settings.alerts.deleteRuleBody')}
                confirm={t('settings.alerts.delete')}
                busy={remove.isPending}
                buttonClass="btn danger"
                onConfirm={() =>
                  remove.mutate(r.id, {
                    onSuccess: () => navigate({ to: '/o/$orgId/p/$projectId/alerts', params }),
                  })
                }
              />
              {/* An SLO's top crash issue is first in its Crash issues section. */}
              {openIssue && r.preset !== 'slo' ? (
                <Link
                  className={btnClass('solid')}
                  to="/o/$orgId/p/$projectId/issues/$issueId"
                  params={{ ...params, issueId: openIssue }}
                  translate="no"
                >
                  {t('alerts.openIssue', { id: openIssue.slice(0, 8) })}
                </Link>
              ) : null}
            </>
          ) : undefined
        }
      />
      <PlanRequired error={update.error} />
      <PausedByPlan rules={[r]} />
      {test.data || test.error ? (
        <p role={test.error ? 'alert' : 'status'} className="al-note">
          {test.error
            ? isApiError(test.error, 429)
              ? t('settings.alerts.testTooMany')
              : t('state.failed')
            : test.data?.delivery === 'sent'
              ? t('settings.alerts.testSent')
              : t('settings.alerts.testFailed', { code: test.data?.error_code ?? '' })}
        </p>
      ) : null}
      <Strip items={strip} translate="no" />
      <div className="grid2">
        <div className="al-stack">
          <Section
            title={
              series.data?.endpoint
                ? t('alerts.chart.endpoint', {
                    endpoint: `${series.data.endpoint.method} ${series.data.endpoint.template || '/'}`,
                  })
                : t(CHART_TITLE[r.preset])
            }
            meta={
              r.preset === 'slo'
                ? [
                    typeof r.params.release === 'string' ? r.params.release : null,
                    sloChartMeta(slo, i18n),
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : series.data
                  ? [
                      typeof r.params.release === 'string' ? r.params.release : null,
                      series.data.step === 'hour'
                        ? t('alerts.chart.hourly')
                        : t('alerts.chart.daily'),
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : undefined
            }
          >
            {slo && slo.measured_at === null ? (
              // Started over or off: the strip's words, not "not enough data" (Loom's N1).
              <p className="dim al-p">
                {r.enabled ? t('alerts.slo.notMeasuredYet') : t('alerts.slo.notMeasuredOff')}
              </p>
            ) : (
              <RuleChart
                query={series}
                share={r.preset === 'slo'}
                mark={
                  slo?.latest_release
                    ? { at: slo.latest_release.since, label: slo.latest_release.release }
                    : undefined
                }
              />
            )}
          </Section>
          {slo && slo.measured_at !== null ? (
            <Section title={t('alerts.slo.burnTable')} meta={t('alerts.slo.burnMeta')}>
              <SloBurnTable slo={slo} destinations={dests} />
              <p className="al-note">{t('alerts.slo.arrival')}</p>
            </Section>
          ) : null}
          <Section
            title={t('alerts.fired')}
            meta={
              r.preset === 'slo'
                ? t('alerts.firedMeta', { count: sloSpells.length, days: FIRED_DAYS })
                : m
                  ? t('alerts.firedMeta', { count: runs.length, days: FIRED_DAYS })
                  : t('alerts.sentMeta', { count: sends.length, days: FIRED_DAYS })
            }
          >
            {events.isPending ? (
              <Loading />
            ) : events.isError ? (
              <ErrorState error={events.error} retry={() => events.refetch()} />
            ) : r.preset === 'slo' ? (
              sloSpells.length === 0 ? (
                <EmptyNote title={t('alerts.notFired')} />
              ) : (
                <SloFired runs={sloSpells} now={now} slo={slo} />
              )
            ) : m ? (
              runs.length === 0 ? (
                <EmptyNote title={t('alerts.notFired')} />
              ) : (
                // Its scroll region is named apart from its section (axe landmark-unique).
                <Table label={t('alerts.firedTable')} className="al-tbl">
                  <thead>
                    <tr>
                      <th scope="col">{t('alerts.col.when')}</th>
                      <th scope="col">{t('alerts.col.valueAt')}</th>
                      <th scope="col">{t('releases.col.release')}</th>
                      <th scope="col">{t('alerts.col.lasted')}</th>
                      <th scope="col">{t('settings.alerts.delivery')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map(({ fired, resolved }) => {
                      const d = DELIVERY[fired.delivery]
                      const release =
                        typeof fired.detail.release === 'string' ? fired.detail.release : ''
                      return (
                        <tr key={fired.id}>
                          <td>{dateTime(i18n, fired.created_at)}</td>
                          <td translate="no">
                            {fired.value === null ? '—' : formatMeasure(i18n, fired.value, m.unit)}
                          </td>
                          <td translate="no">{release || <span className="faint">—</span>}</td>
                          <td>
                            {resolved ? (
                              lasted(
                                i18n,
                                Date.parse(resolved.created_at) - Date.parse(fired.created_at),
                              )
                            ) : (
                              <span className="c-crash">{t('alerts.ongoing')}</span>
                            )}
                          </td>
                          <td>
                            <Status s={d.s} label={t(d.label)} />
                            {fired.error_code ? (
                              <span className="t2" translate="no">
                                {fired.error_code}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )
            ) : recent.length === 0 ? (
              <EmptyNote title={t('alerts.notSent')} />
            ) : (
              <EventFeed events={recent} rules={rules.data.rules} destinations={dests} noRule />
            )}
          </Section>
          {crashIssues.data && crashIssues.data.issues.length > 0 ? (
            <Section
              title={t('alerts.crashIssues')}
              meta={
                typeof r.params.release === 'string'
                  ? t('alerts.crashIssuesMeta', { release: r.params.release })
                  : t('alerts.crashIssuesMetaAll')
              }
            >
              <IssueTable label={t('alerts.crashIssuesTable')} issues={crashIssues.data.issues} />
            </Section>
          ) : null}
          {issues.length > 0 ? (
            <Section
              title={t('alerts.linked')}
              meta={t('alerts.linkedMeta', { count: issues.length })}
            >
              <ul className="rl al-rl">
                {issues.map((e) => {
                  const id = eventIssue(e) as string
                  return (
                    <li key={id}>
                      <Link
                        className="ul"
                        to="/o/$orgId/p/$projectId/issues/$issueId"
                        params={{ ...params, issueId: id }}
                        translate="no"
                      >
                        {id.slice(0, 8)}
                      </Link>
                      <span>
                        {eventSummary(e, i18n)} · {dateTime(i18n, e.created_at)}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </Section>
          ) : null}
        </div>
        <aside className="al-stack al-side" aria-label={t('settings.alerts.rule')}>
          {slo ? (
            <Section title={t('alerts.slo.routing')} meta={t('alerts.slo.routingMeta')}>
              <SloRouting slo={slo} destinations={dests} />
            </Section>
          ) : null}
          <Section
            title={t('alerts.sendTo')}
            right={
              <Link
                className={btnClass(undefined, 'sm')}
                to="/o/$orgId/p/$projectId/settings/$section"
                params={{ ...params, section: 'alerts' }}
              >
                {t('alerts.edit')}
              </Link>
            }
          >
            {dest ? (
              <KV
                items={[
                  [t('settings.alerts.name'), dest.name],
                  [t('settings.alerts.kind'), t(KIND_LABEL[dest.kind])],
                  [
                    t('settings.alerts.host'),
                    <span key="h" translate="no">
                      {dest.host}
                    </span>,
                  ],
                  [
                    t('alerts.health'),
                    dest.consecutive_failures > 0 ? (
                      <span key="f" className="c-crash">
                        {t('settings.alerts.failing', {
                          n: dest.consecutive_failures,
                          code: dest.last_error_code ?? '',
                        })}
                      </span>
                    ) : dest.last_delivered_at ? (
                      t('alerts.lastDelivered', { time: dateTime(i18n, dest.last_delivered_at) })
                    ) : (
                      t('alerts.nothingSent')
                    ),
                  ],
                ]}
              />
            ) : (
              <SentTo
                rule={r}
                destinations={dests}
                canEdit={canEdit}
                busy={update.isPending}
                onPick={(d) => update.mutate({ ruleId: r.id, destination_id: d })}
              />
            )}
          </Section>
          <Section
            title={t('settings.alerts.rule')}
            meta={<span translate="no">{r.id.slice(0, 8)}</span>}
          >
            <KV
              items={[
                [t('settings.alerts.preset'), t(PRESET_LABEL[r.preset])],
                ...paramOrder(r.preset).flatMap(
                  ({ choice: c, field: f }): (readonly [ReactNode, ReactNode])[] =>
                    c
                      ? [
                          [
                            t(c.label),
                            t(
                              (c.options.find((o) => o.value === r.params[c.key]) ?? c.options[0])
                                ?.label ?? c.label,
                            ),
                          ] as const,
                        ]
                      : [
                          [
                            t(fieldLabel(r.preset, f.key)),
                            <span key={f.key} translate="no">
                              {fieldValue(i18n, f.key, num(r.params[f.key]), r.params)}
                            </span>,
                          ] as const,
                          // What the objective means: the share of sessions the budget allows.
                          ...(r.preset === 'slo' && f.key === 'objective'
                            ? [
                                [
                                  t('alerts.kv.budget'),
                                  <span key="budget" translate="no">
                                    {t('alerts.slo.budgetValue', {
                                      pct: i18n.number(100 - num(r.params.objective), {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }),
                                    })}
                                  </span>,
                                ] as const,
                              ]
                            : []),
                        ],
                ),
                ...(takesRelease(r.preset, r.params)
                  ? ([
                      [
                        t('releases.col.release'),
                        typeof r.params.release === 'string' ? (
                          <span key="r" translate="no">
                            {r.params.release}
                          </span>
                        ) : (
                          t('alerts.anyRelease')
                        ),
                      ],
                    ] as const)
                  : []),
                [
                  t('settings.alerts.cooldown'),
                  t('alerts.unit.minutes', { n: r.cooldown_minutes }),
                ],
                [t('alerts.checkedLabel'), t('alerts.every', { every: checkedEvery(r.preset) })],
                [
                  t('alerts.created'),
                  [
                    owner ? owner.name || owner.email : r.seeded ? t('alerts.default') : null,
                    i18n.date(r.created_at, { dateStyle: 'medium' }),
                  ]
                    .filter(Boolean)
                    .join(' · '),
                ],
              ]}
            />
          </Section>
          <Section title={t('alerts.handoff')} minor>
            <p className="al-p">
              {HANDOFF_PRESETS.includes(r.preset) ? (
                <>
                  {dest && isHandoff(dest) ? t('alerts.handoffOn') : t('alerts.handoffCan')}{' '}
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/settings/$section"
                    params={{ ...params, section: 'agents' }}
                  >
                    {t('settings.tab.agents')}
                  </Link>
                </>
              ) : (
                t('alerts.handoffNot')
              )}
            </p>
          </Section>
        </aside>
      </div>
    </main>
  )
}

/** What an SLO watches, in Closest to firing: `error budget · 28 d · 38 % left`. */
function sloWatching(slo: AlertSlo, i18n: Translator): string {
  const left = slo.measured_at === null ? null : leftPercent(slo.budget.left)
  return left
    ? i18n.t('alerts.slo.watching', {
        days: slo.window_days,
        left: i18n.number(left.value, { maximumFractionDigits: 0 }),
      })
    : i18n.t('alerts.slo.watchingBudget', { days: slo.window_days })
}

/** The rule's measure against its threshold: a line for a rate or a spike, bars for messages. */
function RuleChart({
  query,
  mark,
  share,
}: {
  query: UseQueryResult<AlertRuleSeries>
  /** A day to mark on a daily chart (an SLO's newest release, from its first day). */
  mark?: { at: string; label: string } | undefined
  /** A share on 0 to 100 %, so a budget's fall reads at its true size (an SLO). */
  share?: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  if (query.isPending) return <Loading />
  if (query.isError) return <ErrorState error={query.error} retry={() => query.refetch()} />
  const s = query.data
  const label = (iso: string) =>
    s.step === 'hour'
      ? i18n.date(iso, { hour: '2-digit', minute: '2-digit' })
      : i18n.date(iso, { month: 'short', day: 'numeric' })
  if (s.points.every((p) => p.value === null))
    return <p className="dim al-p">{t('alerts.chart.empty')}</p>
  if (s.unit === 'count')
    return (
      <div className="pf-chart">
        <Bars
          values={s.points.map((p) => p.value ?? 0)}
          // Counts tick at whole numbers: an even top, so its half is one too.
          max={(() => {
            const top = Math.max(2, Math.ceil(Math.max(...s.points.map((p) => p.value ?? 0))))
            return top % 2 === 0 ? top : top + 1
          })()}
          label={t('alerts.chart.countLabel', { days: s.points.length })}
          height={180}
          format={(v) => i18n.number(Math.round(v))}
          xLabels={[
            [0, label(s.points[0]?.t ?? '')],
            [s.points.length - 1, label(s.points.at(-1)?.t ?? '')],
          ]}
        />
      </div>
    )
  const scale = s.unit === 'ratio' ? 100 : 1
  const fmt = (v: number) =>
    s.unit === 'ratio'
      ? `${i18n.number(v, { maximumFractionDigits: 2 })} %`
      : s.unit === 'percent'
        ? `${v > 0 ? '+' : ''}${i18n.number(v, { maximumFractionDigits: 1 })} %`
        : `${i18n.number(v, { maximumFractionDigits: 1 })}×`
  // Marked on its first day, and only when that day is on the chart.
  const markAt =
    mark && Date.parse(mark.at) >= Date.parse(s.points[0]?.t ?? '')
      ? s.points.findIndex((p) => Date.parse(p.t) >= Date.parse(mark.at))
      : -1
  return (
    <div className="pf-chart">
      <LineChart
        label={t('alerts.chart.lineLabel', { n: s.points.length })}
        accent
        span={share ? [0, 100] : undefined}
        marker={mark && markAt >= 0 ? { index: markAt, label: mark.label } : undefined}
        points={s.points.map((p) => ({
          label: label(p.t),
          value: p.value === null ? null : p.value * scale,
        }))}
        threshold={
          s.threshold === null
            ? undefined
            : { value: s.threshold * scale, label: fmt(s.threshold * scale) }
        }
        format={fmt}
      />
    </div>
  )
}
